/**
 * Résolution des colonnes — étape 2 de l'import DNS.
 *
 * Un dictionnaire de synonymes par champ canonique, appliqué au libellé
 * normalisé, en correspondance **par préfixe**. Sur les 390 fichiers
 * exploitables du corpus, cela résout `nom` dans 91 % des cas, et
 * `nom` + `matricule` dans 85 %.
 *
 * Les 32 fichiers restants ne se résolvent pas par enrichissement du
 * dictionnaire : leurs colonnes portent des noms propres à l'entreprise,
 * ou l'en-tête n'existe pas. Ils demandent un mapping humain — c'est
 * l'objet de l'Écran 4, et c'est pour cela que cet outil n'est pas un
 * confort mais le moteur de la migration.
 *
 * Ce module est **pur** : il ne lit aucun fichier. Il prend des libellés
 * d'en-tête et rend un mapping, ce qui le rend utilisable aussi bien par
 * l'import serveur que par l'aperçu à l'écran.
 */
import { CHAMPS_RESOLUBLES } from './pivot.js';
/**
 * Majuscules, accents retirés, ponctuation réduite à l'espace, espaces
 * réduits à un seul.
 *
 * `Date d'entrée` et `DATE D ENTREE` doivent produire le même libellé,
 * sans quoi le dictionnaire devrait énumérer toutes les graphies.
 */
export function normaliserLibelle(libelle) {
    return libelle
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, ' ')
        .trim();
}
/**
 * Les synonymes, par champ. Écrits déjà normalisés.
 *
 * Ordre indifférent : c'est le synonyme le PLUS LONG qui gagne, pas le
 * premier trouvé. Sans cette règle, `DATE` — préfixe commun à `DATE DE
 * NAISSANCE`, `DATE ENTREE` et `DATE DEPART` — rattacherait les trois
 * colonnes au même champ.
 */
export const SYNONYMES = {
    matricule: ['MATRICULE', 'MLE', 'N MLE', 'NO MLE', 'N MATRICULE', 'NUMERO MATRICULE'],
    nom: [
        'NOM',
        'NOMS',
        'NOM ET PRENOM',
        'NOM ET PRENOMS',
        'NOM ET PRENOM DU TRAVAILLEUR',
        'NOM PRENOM',
        'NOM DU SALARIE',
        'NOM DU TRAVAILLEUR',
    ],
    prenom: ['PRENOM', 'PRENOMS'],
    fonction: ['FONCTION', 'POSTE', 'ROLE', 'EMPLOI', 'CATEGORIE PROFESSIONNELLE'],
    estAgent: ['S', 'A', 'AGENT', 'SALARIE', 'TITULAIRE', 'AFFILIATION AGENT'],
    nbConjoint: ['C', 'CONJOINT', 'CONJOINTS', 'NB CONJOINT', 'NOMBRE DE CONJOINT', 'EPOUX'],
    nbEnfant: ['E', 'ENFANT', 'ENFANTS', 'NB ENFANT', 'NOMBRE D ENFANT'],
    dateNaissance: ['DATE DE NAISSANCE', 'DATE NAISSANCE', 'NE LE', 'NAISSANCE', 'DDN'],
    sexe: ['SEXE', 'GENRE'],
    lieuAffectation: ['LIEU D AFFECTATION', 'LIEU AFFECTATION', 'AFFECTATION', 'SITE', 'LIEU'],
    dateEntree: [
        'DATE D ENTREE',
        'DATE ENTREE',
        'ENTREE',
        'DATE D EMBAUCHE',
        'DATE EMBAUCHE',
        'EMBAUCHE',
    ],
    dateDepart: ['DATE DE DEPART', 'DATE DEPART', 'DEPART', 'SORTIE', 'DATE DE SORTIE'],
    /**
     * Mesuré le 21/09/2026 sur 342 DNS réels : 84 % portent une colonne de
     * salaire. Le libellé le plus fréquent est « SALAIRE » tout court (159),
     * puis « SALAIRE BRUT » (108), « TOTAL SALAIRE BRUT » (22), « SALAIRE BRUT
     * PAYE » (9), « BRUT » (7). « SALAIRE » seul vaut « brut » : c'est ce que
     * déclare une DNS — la déclaration à la CNAPS est une déclaration de brut.
     *
     * Ce qui n'est PAS le brut est écarté par `NE_SONT_PAS_LE_BRUT`, plus bas.
     */
    salaireBrut: [
        'SALAIRE BRUT',
        'SALAIRES BRUTS',
        'SALAIRE BRUT PAYE',
        'SAL BRUT',
        'BRUT',
        'TOTAL SALAIRE BRUT',
        'TOTAL SALAIRE BRUT DU TRIMESTRE',
        'SALAIRE BRUT DU TRIMESTRE',
        'REMUNERATION BRUTE',
        'SALAIRE',
        'SALAIRES',
        'TOTAL SALAIRE',
        'TOTAL SAL',
        // S42 D2 (16/10) — « TOTAUX SALAIRES NON PLAFONNES » : le brut, au pluriel.
        'TOTAL SALAIRES',
        'TOTAUX SALAIRES',
        'TOT SALAIRE BRUT',
    ],
    /**
     * Relevé le 23/09/2026 : un DNS liste chaque conjoint et enfant sur une
     * sous-ligne, sous son salarié, avec son nom dans une colonne à lui. Plus
     * longs que « NOM » et « PRENOM », ces libellés l'emportent sur eux.
     */
    nomAyantDroit: [
        'NOM MEMBRE FAMILLE',
        'NOM MEMBRE DE LA FAMILLE',
        'NOM DU MEMBRE DE LA FAMILLE',
        'NOM AYANT DROIT',
        'NOM DE L AYANT DROIT',
        'NOM DES AYANTS DROIT',
    ],
    prenomAyantDroit: [
        'PRENOM MEMBRE FAMILLE',
        'PRENOMS MEMBRE FAMILLE',
        'PRENOM MEMBRE DE LA FAMILLE',
        'PRENOMS MEMBRE DE LA FAMILLE',
        'PRENOM AYANT DROIT',
        'PRENOMS AYANT DROIT',
        'PRENOM DE L AYANT DROIT',
    ],
};
/**
 * Ce qui ressemble à un salaire sans être le brut — et qu'on ne prend donc
 * JAMAIS pour lui. Chaque motif vient d'un libellé réel du corpus.
 *
 * - Le salaire de BASE : sur un DNS réel, 264 200 Ar de base pour 361 888 Ar
 *   de brut. Le prendre pour le brut ferait facturer 27 % de moins.
 * - Le NET : après retenues, encore plus bas.
 * - Le salaire PLAFONNÉ : le plafond CNAPS, pas ce que l'employé gagne.
 * - Le salaire d'UN des mois d'un trimestre (« SALAIRE 2EME MOIS ») : le
 *   prendre pour le salaire de la période ferait facturer un tiers du dû.
 * - Une PART DE COTISATION. Le gabarit officiel d'AFS écrit « SALAIRE /
 *   BRUT » puis « COTISATION / EMPLOYE 1% | EMPLOYEUR 5% » ; certaines
 *   entreprises recopient « SALAIRE BRUT » au-dessus des deux parts. Une
 *   part à 1 % prise pour le salaire ferait facturer cent fois moins.
 */
const NE_SONT_PAS_LE_BRUT = [
    /^SAL(AIRE)?S? (DE )?BASE\b/,
    /\bNET\b/,
    // « NON PLAFONNÉ » est le contraire : le brut entier (S42 D2, 16/10).
    /(?<!\bNON )\bPLAF/,
    /\b(\d+|1ER|PREMIER|DEUXIEME|TROISIEME|\d+E|\d+EME)\s+MOIS\b/,
    /\bCOTISATIONS?\b/,
    /\bPARTS?\b/,
    /\bEMPLOYEU?R?S?\b/,
];
/**
 * Une DNS trimestrielle déclare trois mois à la fois. Le salaire qu'elle
 * porte couvre donc trois mois, et la cotisation de la période aussi : le
 * recouvrement doit le savoir, sans quoi il facturerait trois fois moins —
 * ou trois fois plus, selon le sens de l'erreur.
 */
const TRIMESTRE = /\bTRIMESTRE\b/;
const MOIS_DU_TRIMESTRE = /\b(1ER|PREMIER|2EME|DEUXIEME|3EME|TROISIEME|[123]E?)\s+MOIS\b/g;
const RANG_DU_MOIS = {
    '1ER': 1,
    PREMIER: 1,
    '1E': 1,
    '1': 1,
    '2EME': 2,
    DEUXIEME: 2,
    '2E': 2,
    '2': 2,
    '3EME': 3,
    TROISIEME: 3,
    '3E': 3,
    '3': 3,
};
/**
 * S36b (16/10) — un libellé qui parle d'un salaire, reconnu ou non. Sert à ne
 * jamais laisser un salaire de côté en silence : si une telle colonne existe
 * et qu'aucune n'est retenue comme brut, la chargée désigne la colonne.
 */
const MOT_DE_SALAIRE = /\b(SALAIRES?|SAL|BRUTS?|BRUTE|REMUNERATIONS?)\b/;
export function parleDUnSalaire(libelle) {
    return MOT_DE_SALAIRE.test(normaliserLibelle(libelle));
}
/**
 * Le rang (1, 2 ou 3) du mois d'un trimestre qu'une colonne de salaire porte
 * (« SALAIRE 2ÈME MOIS », « 1ER MOIS SALAIRE BRUT PAYÉ »), `null` si elle n'en
 * porte pas, ou si ce n'est pas un salaire brut (net, cotisation, plafonné…).
 */
export function rangDuMoisDeSalaire(libelle) {
    const l = normaliserLibelle(libelle);
    if (!MOT_DE_SALAIRE.test(l))
        return null;
    const autres = NE_SONT_PAS_LE_BRUT.filter((m) => !m.source.includes('MOIS'));
    if (autres.some((m) => m.test(l)))
        return null;
    const rangs = new Set([...l.matchAll(MOIS_DU_TRIMESTRE)].map((m) => RANG_DU_MOIS[m[1] ?? '']));
    rangs.delete(undefined);
    if (rangs.size !== 1)
        return null;
    const [rang] = [...rangs];
    return rang === 1 || rang === 2 || rang === 3 ? rang : null;
}
/**
 * Combien de mois couvre le salaire d'un fichier : 3 s'il est trimestriel, 1 sinon.
 *
 * Un « 1er MOIS » seul ne suffit pas : des gabarits mensuels le gardent en
 * titre de groupe au-dessus du salaire (DNS « du mois de JUIN » mesurée le
 * 21/09/2026). Il faut le mot « trimestre », ou deux mois différents.
 */
export function moisCouvertsPar(entetes) {
    const libelles = entetes.map(normaliserLibelle);
    if (libelles.some((l) => TRIMESTRE.test(l)))
        return 3;
    const rangs = new Set(libelles.flatMap((l) => [...l.matchAll(MOIS_DU_TRIMESTRE)].map((m) => RANG_DU_MOIS[m[1] ?? ''])));
    rangs.delete(undefined);
    return rangs.size >= 2 ? 3 : 1;
}
/**
 * Parmi plusieurs colonnes candidates au salaire, la plus précise l'emporte :
 * le total du trimestre sur le brut, le brut sur « SALAIRE » tout court.
 * Pour tous les autres champs, c'est la première colonne qui gagne.
 */
function precisionSalaire(libelle) {
    const l = normaliserLibelle(libelle);
    if (/\bTRIMESTRE\b/.test(l))
        return 3;
    if (/\bBRUTS?\b|\bBRUTE\b/.test(l))
        return 2;
    return 1;
}
/** Table plate {synonyme → champ}, triée du plus long au plus court. */
const SYNONYMES_TRIES = Object.entries(SYNONYMES)
    .flatMap(([champ, motifs]) => motifs.map((motif) => ({ motif, champ: champ })))
    .sort((a, b) => b.motif.length - a.motif.length);
/**
 * Le champ désigné par un libellé d'en-tête, ou `null`.
 *
 * Correspondance par préfixe : `DATE DE NAISSANCE DU SALARIE` reconnaît
 * `DATE DE NAISSANCE`. On exige que la limite tombe sur un mot entier,
 * sinon `SEXE` reconnaîtrait `SEXENNAT` et `E` reconnaîtrait tout libellé
 * commençant par un E.
 */
export function champPourLibelle(libelle) {
    const normalise = normaliserLibelle(libelle);
    if (normalise === '')
        return null;
    if (normalise === 'A' && !estLaLettreA(libelle))
        return null;
    const champ = champParSynonyme(normalise);
    if (champ === 'salaireBrut' && NE_SONT_PAS_LE_BRUT.some((motif) => motif.test(normalise))) {
        return null;
    }
    return champ;
}
function champParSynonyme(normalise) {
    for (const { motif, champ } of SYNONYMES_TRIES) {
        if (normalise === motif)
            return champ;
        if (!SEULS_DANS_LEUR_CELLULE.has(motif) && normalise.startsWith(`${motif} `))
            return champ;
    }
    return null;
}
/**
 * Les synonymes qui ne se reconnaissent que seuls dans leur cellule, jamais
 * en tête d'un libellé plus long. Une fois l'accent retiré, la préposition
 * « à » devient « A » : un état de salaire réel écrit « Préavis / à payer »
 * et « Net / à » en en-tête, et « à payer » passait pour la colonne des
 * agents — 70 salariés sur 80 devenaient des enfants (S400, 21/10).
 */
const SEULS_DANS_LEUR_CELLULE = new Set(['A']);
/** La lettre A seule, sans accent (« A », « a », « A. ») — jamais « à ». */
function estLaLettreA(libelle) {
    // Lettres et accents seuls : « à » garde son accent combinant, « A. » perd son point.
    const lettres = libelle.normalize('NFD').replace(/[^\p{L}\p{M}]/gu, '');
    return lettres === 'A' || lettres === 'a';
}
/**
 * Résout une ligne d'en-tête.
 *
 * Les cellules vides sont ignorées sans bruit : les gabarits réels sont
 * pleins de colonnes de séparation.
 */
export function resoudreColonnes(entetes) {
    const mapping = {};
    const nonReconnues = [];
    const doublons = [];
    entetes.forEach((libelle, index) => {
        if (normaliserLibelle(libelle) === '')
            return;
        const champ = champPourLibelle(libelle);
        if (champ === null) {
            nonReconnues.push({ index, libelle });
            return;
        }
        const dejaVu = mapping[champ];
        if (champ === 'salaireBrut' &&
            dejaVu !== undefined &&
            precisionSalaire(libelle) > precisionSalaire(entetes[dejaVu] ?? '')) {
            // Le total du trimestre vient souvent APRÈS les colonnes du brut : il
            // doit l'emporter quand même.
            doublons.push({
                champ,
                indexRetenu: index,
                indexIgnore: dejaVu,
                libelleIgnore: entetes[dejaVu] ?? '',
            });
            mapping[champ] = index;
            return;
        }
        if (dejaVu !== undefined) {
            doublons.push({ champ, indexRetenu: dejaVu, indexIgnore: index, libelleIgnore: libelle });
            return;
        }
        mapping[champ] = index;
    });
    return {
        mapping,
        nonReconnues,
        doublons,
        champsObligatoiresAbsents: mapping.nom === undefined ? ['nom'] : [],
    };
}
/**
 * Score de reconnaissance d'une ligne — le nombre de champs canoniques
 * qu'elle désigne.
 *
 * Sert deux fois à l'étape de détection : à choisir la feuille utile parmi
 * les 93 d'un classeur, et à trouver la ligne d'en-tête, qui va de la
 * ligne 1 à la ligne 41 selon les fichiers.
 */
export function scoreEntete(cellules) {
    // Le salaire ne vote pas. Il sert au mapping, une fois l'en-tête trouvé,
    // mais pas à le trouver : le laisser compter déplaçait la ligne d'en-tête
    // du gabarit officiel (« DATE DE » au-dessus de « NAISSANCE ») d'un cran
    // vers le haut, et la date de naissance disparaissait. Mesuré le
    // 21/09/2026 sur les 362 classeurs du corpus : sans ce vote, la détection
    // est strictement celle d'avant.
    return Object.keys(resoudreColonnes(cellules).mapping).filter((c) => c !== 'salaireBrut').length;
}
/**
 * Une ligne ressemble-t-elle à un en-tête plutôt qu'à une donnée ?
 *
 * Le gabarit officiel écrit son en-tête sur DEUX lignes (`DATE DE` puis
 * `NAISSANCE`). Fusionner systématiquement casse les fichiers à en-tête
 * simple : on obtient `NOM <première valeur de la colonne>`. La fusion doit
 * donc être conditionnelle, et ce prédicat est la condition — sur le
 * corpus, ce seul détail fait passer la reconnaissance de `NOM` de 75 % à
 * 91 %.
 *
 * Une ligne de données contient des dates ou des nombres, et remplit
 * beaucoup de cellules. Une seconde ligne d'en-tête est courte et
 * textuelle.
 */
export function ressembleAUnEntete(cellules) {
    const remplies = cellules.filter((c) => c.trim() !== '');
    if (remplies.length === 0)
        return false;
    if (remplies.some(contientUnChiffre))
        return false;
    // Plus de la moitié des colonnes remplies : c'est une ligne de données…
    if (remplies.length <= Math.max(1, Math.floor(cellules.length / 2)))
        return true;
    // … sauf si elle porte des libellés de colonnes. Certains gabarits posent
    // les groupes sur la première ligne (« AFFILIATION », « DATE ») et toutes
    // les sous-colonnes sur la seconde (« Agent », « Conjoint », « Entrée »…),
    // pleine. Une ligne de données sans un chiffre ne ressemble jamais à trois
    // libellés reconnus à la fois.
    return scoreEntete(cellules) >= LIBELLES_MIN_SECONDE_LIGNE_PLEINE;
}
/** Libellés reconnus au-delà desquels une seconde ligne pleine reste un en-tête. */
const LIBELLES_MIN_SECONDE_LIGNE_PLEINE = 3;
/**
 * Un chiffre de donnée. Un taux n'en est pas un : le gabarit officiel écrit
 * « EMPLOYE 1% » et « EMPLOYEUR 5% » sur la ligne de « NAISSANCE ».
 */
function contientUnChiffre(cellule) {
    return /\d/.test(cellule.replace(/\d+(?:[.,]\d+)?\s*%/g, ''));
}
/**
 * Fusionne une ligne d'en-tête avec la suivante, quand celle-ci est la
 * seconde moitié d'un en-tête sur deux lignes.
 */
export function fusionnerEntete(premiere, seconde) {
    if (!ressembleAUnEntete(seconde))
        return premiere;
    const largeur = Math.max(premiere.length, seconde.length);
    return Array.from({ length: largeur }, (_, i) => {
        const haut = (premiere[i] ?? '').trim();
        const bas = (seconde[i] ?? '').trim();
        const joint = [haut, bas].filter(Boolean).join(' ');
        // Un haut répété sur la colonne voisine est une cellule fusionnée : un
        // titre de groupe. Il gâte (« AFFILIATION Conjoint ») ou détourne
        // (« Epoux(se) N° CIN » n'est pas un nombre de conjoints) le libellé
        // qu'il coiffe : sous un groupe, seul le bas compte.
        const groupe = haut !== '' &&
            ((premiere[i - 1] ?? '').trim() === haut || (premiere[i + 1] ?? '').trim() === haut);
        if (groupe && bas !== '')
            return bas;
        // Le bas, s'il dit quelque chose à lui seul, l'emporte aussi — sauf si
        // le tout dit la même chose, en mieux : « DATE DE » + « NAISSANCE ».
        // Sinon, c'est le tout : « LIEU » + « D'AFFECTATION ».
        const champDuBas = champPourLibelle(bas);
        return champDuBas === null || champPourLibelle(joint) === champDuBas ? joint : bas;
    });
}
/** Les champs qu'aucun synonyme ne couvre — utile aux tests de couverture. */
export const CHAMPS_SANS_SYNONYME = CHAMPS_RESOLUBLES.filter((champ) => SYNONYMES[champ].length === 0);
//# sourceMappingURL=synonymes.js.map